// Admin tab — sortable grid with state-aware per-row Pull/Reclassify
// (SGNL-83), with column-header sort + post-success state mutation
// (SGNL-85).
//
// Operators get the global filter bar + a sortable grid (Account /
// Opportunity / Owner / Stage / Close / Last classified) with a per-row
// action button:
//   classified_at = null → "Pull & Classify" (primary)
//   classified_at set    → "Refetch & Reclassify" (secondary)
//
// On a successful click we do BOTH: paint the inline result into the
// row (preserves scroll, shows pull counts), AND mutate the opp's
// classified_at on the in-memory bundle so a tab-switch re-render
// reflects the new state instead of reverting to "Pull & Classify".
//
// Endpoint: POST /api/admin/opps/{opp_id}/pull-and-reclassify (SGNL-25).
// Server-gated by current_admin; non-admins get 403 → surfaced inline.

import { esc } from "../lib/format";
import {
  bindSortHeaders,
  makeSortable,
  type SortColumn,
  type SortState,
} from "../lib/sortable";
import { getState, updateOppClassifiedAt } from "../state";
import type { OpportunityRow } from "../types";
import { renderAIAuditPanel } from "./ai_audit";
import { renderAdminAppsPanel } from "./admin_apps";
import { renderMcpKeysPanel } from "./admin_mcp_keys";
import { renderRevenueExclusionsPanel } from "./admin_revenue_exclusions";
import { renderAdminUsersPanel } from "./admin_users";

// SGNL-134 — Admin tab sub-tabs. "reclassify" is the historical per-opp
// pull-and-classify grid; "ai-audit" is the new rep-vs-LLM side-by-side
// view; "users" (SGNL-34) is the platform RBAC management surface.
type AdminSubtab =
  | "reclassify"
  | "ai-audit"
  | "users"
  | "apps"
  | "revenue-exclusions"
  | "mcp-access";
let activeAdminSubtab: AdminSubtab = "reclassify";

interface ReclassifyResult {
  opp_id: string;
  pulled: {
    tasks: number;
    chatter: number;
    events: number;
    notes: number;
    emails: number;
  };
  enqueued: boolean;
  classified: boolean;
  // SGNL-87 — server-stamped classification timestamp. Nullable
  // because the classifier may skip stamping (e.g. classification
  // ran but produced no verdict change). Optional in the type so
  // older BE deploys missing the field don't fail typed parsing.
  classified_at?: string | null;
}

async function callReclassify(oppId: string): Promise<ReclassifyResult> {
  const r = await fetch(
    `/api/admin/opps/${encodeURIComponent(oppId)}/pull-and-reclassify`,
    {
      method: "POST",
      credentials: "same-origin",
    },
  );
  if (r.status === 403) {
    throw new Error(
      "403 — your account isn't in BOOTSTRAP_ADMINS. Ask an admin to add your email.",
    );
  }
  if (!r.ok) {
    // Read body once as text — calling .json() then falling back to .text()
    // doesn't work because the body stream is consumed by the first call.
    // PR-Agent finding (importance 6) on PR #34.
    const raw = await r.text();
    let body = raw;
    try {
      body = JSON.stringify(JSON.parse(raw));
    } catch {
      // not valid JSON — surface raw text
    }
    throw new Error(`HTTP ${r.status}: ${body}`);
  }
  return (await r.json()) as ReclassifyResult;
}

function fmtClassifiedAt(ts: string | null | undefined): string {
  if (!ts) return "never";
  // ISO → 'YYYY-MM-DD HH:MM' for compact display.
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "never";
  const iso = d.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

function fmtPullCounts(p: ReclassifyResult["pulled"]): string {
  return `${p.tasks}t · ${p.chatter}c · ${p.events}e · ${p.notes}n · ${p.emails}m`;
}

function actionLabel(opp: OpportunityRow): string {
  return opp.classified_at ? "Refetch & Reclassify" : "Pull & Classify";
}

function actionClass(opp: OpportunityRow): string {
  return opp.classified_at ? "btn-secondary" : "btn-primary";
}

// Column definitions for the shared sortable helper. The Action column
// is non-sortable (`sortable: false`) and right-aligned (`numeric`). The
// classified_at accessor returns "" for null so asc-sort surfaces the
// never-classified rows at the top — that's the operator's priority
// queue (rows that need to be pulled).
const ADMIN_COLUMNS: SortColumn<OpportunityRow>[] = [
  { key: "account_name", label: "Account" },
  { key: "name", label: "Opportunity" },
  { key: "owner_name", label: "Owner" },
  { key: "stage", label: "Stage" },
  { key: "close_date", label: "Close" },
  {
    key: "classified_at",
    label: "Last classified",
    accessor: (o) => o.classified_at ?? "",
  },
  { key: "id", label: "Action", numeric: true, sortable: false },
];

// Module-level sort state — preserved across re-renders within the tab
// session. Default is classified_at asc (never-classified rows first).
let sortState: SortState<OpportunityRow> = {
  field: "classified_at",
  dir: "asc",
};

function renderGrid(opps: OpportunityRow[]): string {
  if (opps.length === 0) {
    return `<p class="empty-bucket">No opportunities match the current filters.</p>`;
  }
  // Cap rendered rows to keep the DOM responsive — same convention as
  // Deal explorer's 500-row cap.
  const { sorted, headHtml } = makeSortable(opps, ADMIN_COLUMNS, sortState);
  const capped = sorted.slice(0, 500);
  return `
    <div class="table-wrap">
      <table class="data-table sortable" data-testid="admin-grid">
        ${headHtml}
        <tbody>
          ${capped
            .map(
              (o) => `
            <tr data-opp-id="${esc(o.id)}" data-testid="admin-row">
              <td>${esc(o.account_name ?? "—")}</td>
              <td><a class="drill" href="#opp/${esc(o.id)}" data-testid="admin-opp-link">${esc(o.name)}</a></td>
              <td>${esc(o.owner_name ?? "—")}</td>
              <td>${esc(o.stage ?? "—")}</td>
              <td>${esc(o.close_date ?? "—")}</td>
              <td class="classified-at">${esc(fmtClassifiedAt(o.classified_at))}</td>
              <td class="num">
                <button
                  type="button"
                  class="${actionClass(o)} admin-action-btn"
                  data-action="reclassify"
                  data-opp-id="${esc(o.id)}"
                  data-testid="admin-action-btn"
                >${esc(actionLabel(o))}</button>
              </td>
            </tr>`,
            )
            .join("")}
        </tbody>
      </table>
    </div>
    ${opps.length > 500 ? `<p class="table-footnote">Showing first 500 of ${opps.length.toLocaleString()} rows. Use the filter bar to narrow further.</p>` : ""}`;
}

function renderInlineProgress(row: HTMLTableRowElement): void {
  const cell = row.querySelector<HTMLTableCellElement>("td.num")!;
  cell.innerHTML = `<span class="reclassify-inline" data-testid="admin-inline-progress">⏳ pulling…</span>`;
}

function renderInlineResult(
  row: HTMLTableRowElement,
  result: ReclassifyResult,
): void {
  const cell = row.querySelector<HTMLTableCellElement>("td.num")!;
  cell.innerHTML = `
    <span class="reclassify-inline reclassify-success" data-testid="admin-inline-result">
      ✓ ${esc(fmtPullCounts(result.pulled))}
    </span>
    <button
      type="button"
      class="btn-secondary admin-action-btn"
      data-action="reclassify"
      data-opp-id="${esc(result.opp_id)}"
      data-testid="admin-action-btn"
    >Refetch &amp; Reclassify</button>`;
  // Update the "Last classified" cell — best-effort, just shows "now"
  // since the orchestrator just ran.
  const classifiedCell =
    row.querySelector<HTMLTableCellElement>("td.classified-at");
  if (classifiedCell) classifiedCell.textContent = "just now";
}

function renderInlineError(
  row: HTMLTableRowElement,
  message: string,
  oppId: string,
): void {
  const cell = row.querySelector<HTMLTableCellElement>("td.num")!;
  cell.innerHTML = `
    <span class="reclassify-inline reclassify-error" data-testid="admin-inline-error" title="${esc(message)}">
      ✗ failed
    </span>
    <button
      type="button"
      class="btn-secondary admin-action-btn"
      data-action="reclassify"
      data-opp-id="${esc(oppId)}"
      data-testid="admin-action-btn"
    >Retry</button>`;
}

function subtabNav(active: AdminSubtab): string {
  // Lightweight pill nav above the sub-panel. Two pills today; an
  // `aria-current="page"` marker on the active pill keeps the
  // selection screenreader-accessible without a full ARIA tablist.
  const pill = (key: AdminSubtab, label: string) => `
    <button type="button"
      class="admin-subtab ${active === key ? "active" : ""}"
      data-subtab="${key}"
      data-testid="admin-subtab-${key}"
      ${active === key ? 'aria-current="page"' : ""}
    >${label}</button>`;
  return `
    <nav class="admin-subtabs" data-testid="admin-subtabs">
      ${pill("reclassify", "Pull & Reclassify")}
      ${pill("ai-audit", "AI Audit")}
      ${pill("users", "Users")}
      ${pill("apps", "Apps")}
      ${pill("revenue-exclusions", "Revenue Exclusions")}
      ${pill("mcp-access", "MCP Access")}
    </nav>`;
}

function renderReclassifyPanel(host: HTMLElement, opps: OpportunityRow[]): void {
  host.innerHTML = `
    <p class="muted">
      Per-opp control plane. Pulls every source (tasks / chatter / events / notes / emails)
      from Salesforce, refreshes the Opportunity row itself, summarizes new content via
      Claude, and runs the classifier — all on demand, scoped to one opp at a time.
      Use the global filter bar above to narrow the grid.
    </p>
    <div class="admin-grid-host" data-testid="admin-grid-host">
      ${renderGrid(opps)}
    </div>`;

  const gridHost = host.querySelector<HTMLElement>(
    '[data-testid="admin-grid-host"]',
  )!;
  bindSortHeaders(gridHost, ADMIN_COLUMNS, sortState, (next) => {
    sortState = next;
    renderReclassifyPanel(host, opps);
  });

  // Single delegated handler — no per-button listeners. Lets us swap row
  // contents in renderInlineResult without re-binding handlers.
  gridHost.addEventListener("click", async (e) => {
    const target = (e.target as HTMLElement).closest<HTMLButtonElement>(
      'button[data-action="reclassify"]',
    );
    if (!target) return;
    const oppId = target.dataset.oppId;
    if (!oppId) return;
    const row = target.closest<HTMLTableRowElement>("tr[data-opp-id]");
    if (!row) return;

    target.disabled = true;
    renderInlineProgress(row);
    try {
      const result = await callReclassify(oppId);
      renderInlineResult(row, result);
      updateOppClassifiedAt(oppId, result.classified_at ?? new Date().toISOString());
    } catch (err) {
      renderInlineError(row, (err as Error).message, oppId);
    }
  });
}

export function renderAdminTab(
  host: HTMLElement,
  opps: OpportunityRow[],
): void {
  host.innerHTML = `
    <div class="tab-content" data-testid="admin-tab">
      <div class="tab-header">
        <h2>Admin</h2>
      </div>
      ${subtabNav(activeAdminSubtab)}
      <div class="admin-subpanel-host" data-testid="admin-subpanel-host"></div>
    </div>`;

  const panelHost = host.querySelector<HTMLElement>(
    '[data-testid="admin-subpanel-host"]',
  )!;
  if (activeAdminSubtab === "ai-audit") {
    void renderAIAuditPanel(panelHost);
  } else if (activeAdminSubtab === "users") {
    // SGNL-34 — current user's email needed for self-action guard.
    // Pulled from state.ts to avoid an extra /whoami fetch.
    const state = getState();
    const currentEmail = state.user?.email ?? "";
    void renderAdminUsersPanel(panelHost, currentEmail);
  } else if (activeAdminSubtab === "apps") {
    void renderAdminAppsPanel(panelHost);
  } else if (activeAdminSubtab === "revenue-exclusions") {
    void renderRevenueExclusionsPanel(panelHost);
  } else if (activeAdminSubtab === "mcp-access") {
    void renderMcpKeysPanel(panelHost, getState().user?.email ?? "");
  } else {
    renderReclassifyPanel(panelHost, opps);
  }

  // Sub-tab pill clicks — swap the active sub-tab and re-render in
  // place. Doesn't touch the global tab so the operator stays on Admin.
  for (const btn of host.querySelectorAll<HTMLButtonElement>(
    '[data-testid="admin-subtabs"] button[data-subtab]',
  )) {
    btn.addEventListener("click", () => {
      const next = btn.dataset.subtab as AdminSubtab | undefined;
      if (next && next !== activeAdminSubtab) {
        activeAdminSubtab = next;
        renderAdminTab(host, opps);
      }
    });
  }
}

// SGNL-134 test hook — vitest tests set the active sub-tab to a known
// state in beforeEach so module-level persistence doesn't leak between
// cases. Not part of the public surface for production code.
export function _setAdminSubtabForTest(subtab: AdminSubtab): void {
  activeAdminSubtab = subtab;
}
